import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import { FiArrowLeft, FiExternalLink, FiCopy, FiEye, FiUsers, FiCheck, FiCalendar, FiGlobe } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "@/services/api";
import Loader from "@/components/loader";
import { CANDIDATE_URL } from "@/config";

const translations = {
  en: {
    title: "Post Details",
    channel: "Channel",
    url: "Post URL",
    status: "Status",
    created_at: "Created",
    copy_url: "Copy URL",
    copied: "Copied!",
    open_url: "Open in new tab",
    stats_title: "Statistics",
    views: "Views",
    applications: "Applications",
    conversion_rate: "Conversion Rate",
    status_active: "Active",
    status_inactive: "Inactive",
    status_paused: "Paused",
    no_url: "No URL available",
    loading: "Loading post details...",
  },
  fr: {
    title: "Détails du Post",
    channel: "Canal",
    url: "URL du Post",
    status: "Statut",
    created_at: "Créé le",
    copy_url: "Copier l'URL",
    copied: "Copié !",
    open_url: "Ouvrir dans un nouvel onglet",
    stats_title: "Statistiques",
    views: "Vues",
    applications: "Candidatures",
    conversion_rate: "Taux de Conversion",
    status_active: "Actif",
    status_inactive: "Inactif",
    status_paused: "En Pause",
    no_url: "Aucune URL disponible",
    loading: "Chargement des détails du post...",
  }
};

export default function PostDetail() {
  const { t } = useComponentTranslation(translations);
  const { id, postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [stats, setStats] = useState({ views: 0, applications: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPostDetails();
  }, [postId]);

  const fetchPostDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch post details
      const postResponse = await api.get(`/post/${postId}`);
      if (postResponse.ok) {
        setPost(postResponse.data);
        
        // Fetch view statistics using the dedicated route
        try {
          const viewStatsResponse = await api.post(`/view/stats/post/${postId}`, {});
          
          if (viewStatsResponse.ok && viewStatsResponse.data) {
            setStats(prev => ({
              ...prev,
              views: viewStatsResponse.data.total_views || 0,
            }));
          }
        } catch (viewError) {
          console.error("Error fetching view stats:", viewError);
        }
        
        // Fetch applications for this specific post
        try {
          const applicationsResponse = await api.post(`/application/search`, {
            job_post_id: postId,
            limit: 1,
          });
          
          if (applicationsResponse.ok) {
            setStats(prev => ({
              ...prev,
              applications: applicationsResponse.total || 0,
            }));
          }
        } catch (appError) {
          console.error("Error fetching applications:", appError);
        }
      }
    } catch (e) {
      console.error("Error fetching post details:", e);
      toast.error("Error loading post details");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (postUrl) {
      try {
        await navigator.clipboard.writeText(postUrl);
        setCopied(true);
        toast.success(t("copied"));
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        toast.error("Failed to copy URL");
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-700";
      case "inactive":
        return "bg-gray-100 text-gray-700";
      case "paused":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return t("status_active");
      case "inactive":
        return t("status_inactive");
      case "paused":
        return t("status_paused");
      default:
        return status;
    }
  };

  // Construct the post URL
  const postUrl = useMemo(() => {
    if (!post) return null;
    
    // For Jobego posts, construct URL using organization slug
    if (post.organization_slug && post._id) {
      return `${CANDIDATE_URL}/companies/${post.organization_slug}/jobs/${post._id}`;
    }
    
    // Fallback: check if there's a direct URL field
    return post.url || post.external_url || post.link || null;
  }, [post]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(`/recruiter/job-ads/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <FiArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back</span>
        </button>
        <div className="text-center text-gray-500">Post not found</div>
      </div>
    );
  }

  const conversionRate = stats.views > 0 ? ((stats.applications / stats.views) * 100).toFixed(1) : 0;

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruiter/job-ads/${id}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <FiArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back</span>
      </button>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {post.channel_logo ? (
                <img 
                  src={post.channel_logo} 
                  alt={post.channel_name} 
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {post.channel_name?.charAt(0) || "?"}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{post.channel_name}</h1>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(post.status)}`}>
                    {getStatusText(post.status)}
                  </span>
                  {post.created_at && (
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <FiCalendar className="w-4 h-4" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* URL Section */}
          {postUrl ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{t("url")}</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-gray-700 break-all">
                  {postUrl}
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title={t("copy_url")}
                >
                  {copied ? (
                    <FiCheck className="w-5 h-5 text-green-600" />
                  ) : (
                    <FiCopy className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                <a
                  href={postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  title={t("open_url")}
                >
                  <FiExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">{t("no_url")}</p>
            </div>
          )}
        </div>

        {/* Statistics Cards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t("stats_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Views */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">{t("views")}</p>
                <FiEye className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.views || 0}</p>
            </div>

            {/* Applications */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">{t("applications")}</p>
                <FiUsers className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.applications || 0}</p>
            </div>

            {/* Conversion Rate */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">{t("conversion_rate")}</p>
                <FiGlobe className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{conversionRate}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

